import { useState, useCallback } from 'react';

interface JsonErrorHandlerResult {
  error: string | null;
  errorContext: string | null;
  setError: (error: string | null) => void;
  setErrorContext: (context: string | null) => void;
  clearError: () => void;
  getErrorContext: (text: string, errorMessage: string) => string | null;
  handleJsonError: (
    sectionsText: string,
    questionsText: string,
    setActiveTab: (tab: string) => void
  ) => void;
}

export function useJsonErrorHandler(): JsonErrorHandlerResult {
  const [error, setError] = useState<string | null>(null);
  const [errorContext, setErrorContext] = useState<string | null>(null);

  // Extract context around JSON error position
  const getErrorContext = useCallback((text: string, errorMessage: string) => {
    const positionMatch = errorMessage.match(/position (\d+)/);
    const lineMatch = errorMessage.match(/line (\d+)/);
    
    if (!positionMatch && !lineMatch) return null;
    
    const lines = text.split('\n');
    
    if (lineMatch) {
      const lineNumber = parseInt(lineMatch[1], 10) - 1; // Convert to 0-based
      const contextLines = [];
      
      // Show 2 lines before and after the error line
      for (let i = Math.max(0, lineNumber - 2); i <= Math.min(lines.length - 1, lineNumber + 2); i++) {
        const prefix = i === lineNumber ? '>>> ' : '    ';
        contextLines.push(`${prefix}${i + 1}: ${lines[i]}`);
      }
      
      return contextLines.join('\n');
    }
    
    if (positionMatch) {
      const position = parseInt(positionMatch[1], 10);
      const start = Math.max(0, position - 50);
      const end = Math.min(text.length, position + 50);
      const context = text.slice(start, end);
      const errorPos = position - start;
      
      return context.slice(0, errorPos) + ' <<<ERROR>>> ' + context.slice(errorPos);
    }
    
    return null;
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setErrorContext(null);
  }, []);

  // Handle JSON parsing errors and automatically switch to the problematic tab
  const handleJsonError = useCallback((
    sectionsText: string,
    questionsText: string,
    setActiveTab: (tab: string) => void
  ) => {
    // Try to determine which tab has the error and show context
    let context = null;
    try {
      JSON.parse(sectionsText);
    } catch (sectionsError) {
      if (sectionsError instanceof Error) {
        context = getErrorContext(sectionsText, sectionsError.message);
        setActiveTab('sections'); // Switch to the tab with the error
      }
    }
    
    if (!context) {
      try {
        JSON.parse(questionsText);
      } catch (questionsError) {
        if (questionsError instanceof Error) {
          context = getErrorContext(questionsText, questionsError.message);
          setActiveTab('questions'); // Switch to the tab with the error
        }
      }
    }
    
    setErrorContext(context);
  }, [getErrorContext]);

  return {
    error,
    errorContext,
    setError,
    setErrorContext,
    clearError,
    getErrorContext,
    handleJsonError,
  };
}